import * as R from 'ramda';
import { format } from 'date-fns';
// feature analytics
import {
  uomLabelMap,
  PAGE_LANE_RPM,
  LANE_GRAIN_FULL,
  SUB_TABLE_INDENT,
  DEFAULT_STATUSES,
  INITIAL_QUICK_DAYS,
  PAGE_FLEET_ACTIVITY,
} from './constants';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
//////////////////////////////////////////////////

// dispatch binds LocalDate with the MM/dd/yyyy formatter (shared DateFormatters.DATE_FORMATTER),
// NOT ISO — send that format or the request is rejected ("Incorrect value ... for field toDate").
export const toApiDate = (value) => format(new Date(value), 'MM/dd/yyyy');

export const toDisplayDate = (value) => (
  G.isNilOrEmpty(value) ? '-' : format(new Date(value), 'MM/dd/yyyy')
);

// UTC instants (sync-status lastCompletedAt) shown in the branch timezone using
// GENERAL_BRANCH_DATE_TIME_FORMAT — US 12h (`MM/dd/yyyy hh:mm aa`) or US_24H.
export const toDisplayDateTime = (value) => {
  if (G.isNilOrEmpty(value)) return value;

  const parsed = new Date(value);

  if (R.not(G.isValidDate(parsed))) return value;

  const timeZone = G.getAmousConfigByNameFromWindow(GC.GENERAL_BRANCH_TIMEZONE);
  const zoned = G.isNilOrEmpty(timeZone)
    ? parsed
    : new Date(parsed.toLocaleString('en-US', { timeZone }));

  return format(zoned, G.getDateTimeFormatFromConfig());
};

// a numeric column's formatter. Blank when the server sent nothing — an absent rate (miles = 0 ⇒
// null) must never render as a fake 0.
export const decimalFormatter = (minimumFractionDigits, maximumFractionDigits) => (value) => {
  if (G.isNilOrEmpty(value)) return '';

  return Number(value).toLocaleString('en-US', { minimumFractionDigits, maximumFractionDigits });
};

// "City, ST zip" from whatever parts the row carries — missing parts simply drop out
export const composeLocation = (city, state, zip) => {
  const cityState = R.join(', ', R.reject(G.isNilOrEmpty, [city, state]));

  return R.join(' ', R.reject(G.isNilOrEmpty, [cityState, zip]));
};

export const createStatusOptions = (loadType) => R.map(
  (status) => ({ value: status, label: G.getWindowLocale(R.prop(status, GC.statusLocaleMap), status) }),
  R.pathOr([], [loadType], GC.loadStatusesMap),
);

export const uomLabelOf = (report) => R.propOr('mi', R.pathOr('MILE', ['distanceUom'], report), uomLabelMap);

export const appendReportList = (prevReport, nextReport, listKey) => ({
  ...R.or(prevReport, {}),
  ...nextReport,
  [listKey]: [
    ...R.pathOr([], [listKey], prevReport),
    ...R.pathOr([], [listKey], nextReport),
  ],
  grandTotal: R.or(R.path(['grandTotal'], nextReport), R.path(['grandTotal'], prevReport)),
});

// KPI cards use the same fields and formatters as the pinned Grand Total row.
// Featured cards are listed first so they lead the KPI row. A missing total is 0,
// not a blank card — the table still uses decimalFormatter's empty string for nulls.
export const kpiItemsOf = (row, metrics) => {
  const items = R.map((metric) => {
    const {
      key,
      label,
      prefix,
      suffix,
      featured,
      format = R.identity,
    } = metric;

    const raw = R.prop(key, row);
    const text = format(G.isNilOrEmpty(raw) ? 0 : raw);
    const hasText = G.isNotNilAndNotEmpty(text);
    const value = G.isNotNilAndNotEmpty(prefix) && hasText
      ? `${prefix}${text}`
      : text;

    return R.reject(R.isNil, {
      label,
      value,
      featured,
      suffix: hasText ? suffix : undefined,
    });
  }, metrics);

  return [
    ...R.filter(R.prop('featured'), items),
    ...R.reject(R.prop('featured'), items),
  ];
};

// VirtualizedTable's right-actions column (three-dots / Edit View)
export const SUB_TABLE_ACTIONS_WIDTH = 50;

export const columnSettingsWidth = (columnSettings) =>
  R.sum(R.pluck('width', R.values(columnSettings))) + SUB_TABLE_INDENT + SUB_TABLE_ACTIONS_WIDTH;

export const initialsOf = (name) => {
  const parts = R.filter(G.isNotNilAndNotEmpty, R.split(' ', R.trim(R.or(name, ''))));

  return R.toUpper(R.join('', R.map((part) => part[0], R.take(2, parts))));
};

export const rateToneOf = (value, baseline) => {
  if (G.isNilOrEmpty(value)) return null;

  const numeric = Number(value);

  if (R.equals(numeric, 0)) return 'weak';

  if (G.isNilOrEmpty(baseline) || R.equals(Number(baseline), 0)) return 'average';

  const ratio = R.divide(numeric, Number(baseline));

  if (R.gte(ratio, 1.08)) return 'strong';

  if (R.lte(ratio, 0.85)) return 'weak';

  return 'average';
};

// Last N days ending today. The initial range has to be built the same way a quick-day click builds
// it — and to be one of GC.QUICK_DAYS_FILTER — or the switcher renders with nothing highlighted and
// the range the report already queried is invisible.
export const quickDaysRange = (days) => {
  const dateTo = G.getCurrentDay();

  return {
    dateTo,
    quickDaysValue: days,
    dateFrom: G.subtractDateTimeWithFormat(dateTo, R.dec(days), 'days', GC.DEFAULT_DATE_FORMAT),
  };
};

const emptyOptions = () => ({
  drivers: { items: [], loadedFor: null },
  regions: { items: [], loadedFor: null },
});

export const createInitialFilters = (page) => {
  const statuses = DEFAULT_STATUSES;
  const enterpriseGuids = [];
  const includeInactiveEnterprises = true;
  const dateRange = quickDaysRange(INITIAL_QUICK_DAYS);

  if (R.equals(page, PAGE_LANE_RPM)) {
    return {
      statuses,
      dateRange,
      destZip3: [],
      destSearch: '',
      originZip3: [],
      enterpriseGuids,
      originSearch: '',
      destRegionGuids: [],
      originRegionGuids: [],
      includeInactiveEnterprises,
      laneGrain: LANE_GRAIN_FULL,
    };
  }

  if (R.equals(page, PAGE_FLEET_ACTIVITY)) {
    return {
      statuses,
      dateRange,
      driverGuids: [],
      enterpriseGuids,
      includeInactiveEnterprises,
    };
  }

  return {
    statuses,
    dateRange,
    enterpriseGuids,
    destRegionGuids: [],
    originRegionGuids: [],
    includeInactiveEnterprises,
  };
};

export const createPageState = (page) => ({
  reports: {},
  expanded: null,
  options: emptyOptions(),
  filters: createInitialFilters(page),
});

// Navigation can reuse a matching result or let its pending request finish.
export const canReuseReport = (current, payload) => {
  if (!payload.reuseCache || !current) return false;

  const pending = current.loading && !current.append;
  const applied = pending ? R.path(['request', 'payload', 'applied'], current) : current.applied;
  const query = pending ? R.path(['request', 'payload', 'data'], current) : R.prop('query', applied);
  const available = pending || (current.report && (current.phase === 'success' || current.loading));

  return Boolean(available && R.equals(query, payload.data)
    && R.equals(R.prop('filters', applied), R.path(['applied', 'filters'], payload)));
};
